import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Autocomplete, {
  type AutocompleteInputChangeReason,
  type AutocompleteRenderInputParams,
  type AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper, { type PaperProps } from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ClearIcon from '@mui/icons-material/Clear';

import type { BankFeedSummaryAccount } from 'domain/bankFeed/schemas';
import { useBankFeedRouteContext } from 'pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from 'pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import SelectedAccountChip from 'pages/company/BankFeedRoute/components/FilterToolbar/shared/SelectedAccountChip';

// Bank tab "Bank account" toolbar filter (DA-19179) on MUI Autocomplete: checkbox option
// rows with a bank avatar, built-in search, a "Select All" header, apply-on-close, and an
// "All" / chip+counter trigger. Mirrors the VendorExpenses AutocompleteAsyncWithCheckboxes
// UX. Wire contract: toolbarFilters.accountIds, empty = all accounts. The Credit Card tab
// uses a distinct single-value CardAccountFilter (DA-19483).

const ACCOUNTS_SELECT_WIDTH = 280;
// The dropdown popup is wider than the (compact) input so long account labels fit —
// overriding the popper width, which MUI otherwise pins to the anchor width.
const ACCOUNTS_POPUP_WIDTH = 420;
const ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED_ICON = <CheckBoxIcon fontSize="small" />;
const ellipsisSx = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

// Collapsed trigger for a non-empty subset: the first account as a Chip + a "+N" counter
// whose tooltip lists every selected account, one per line (VendorExpenses pattern).
const AccountChips: React.FC<{ picked: BankFeedSummaryAccount[] }> = ({ picked }) => {
  const [first, ...rest] = picked;
  if (!first) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0 }}>
      <SelectedAccountChip label={first.label} />
      {rest.length > 0 && (
        <Tooltip
          placement="bottom-start"
          title={
            <Box>
              {picked.map((p) => (
                <Box key={p.id} sx={{ fontSize: '0.75rem', py: 0.25 }}>
                  {p.label}
                </Box>
              ))}
            </Box>
          }
        >
          <Chip label={`+${rest.length}`} size="small" />
        </Tooltip>
      )}
    </Box>
  );
};

const BankAccountsFilter: React.FC = () => {
  const intl = useIntl();
  const { toolbarFilters, setToolbarFilters, accountType, summary, isSummaryLoading } = useBankFeedRouteContext();

  const { accountIds } = toolbarFilters;

  // Memoised so the option/value references stay stable between renders (MUI Autocomplete
  // requires referential stability for a controlled `value`).
  const options = useMemo(
    () => summary?.items.find((i) => i.accountType === accountType)?.accounts ?? [],
    [summary, accountType],
  );

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Working set of selected options while open. null = all (default; wire []).
  const [checked, setChecked] = useState<BankFeedSummaryAccount[] | null>(null);

  // Drop the draft on tab switch (per-tab account id-spaces).
  useEffect(() => setChecked(null), [accountType]);

  const selected = checked ?? options;

  // Search-scoped selection, mirroring BankFeedSetColumnFilter / Excel: the typed query
  // narrows the visible options, and "apply" (close) commits only the selections visible
  // under it. Identity is the account id (unique per account).
  const search = inputValue.trim().toLowerCase();
  const filteredOptions = useMemo(
    () => (search.length === 0 ? options : options.filter((o) => o.label.toLowerCase().includes(search))),
    [options, search],
  );
  // Selected items visible under the current search — committed instead of the full
  // selection when closing with an active query (hidden selections are dropped).
  const visibleSelected = useMemo(() => {
    if (search.length === 0) return selected;
    const filteredIds = new Set(filteredOptions.map((o) => o.id));
    return selected.filter((s) => filteredIds.has(s.id));
  }, [search, selected, filteredOptions]);
  // Every currently-visible option is selected — drives the "Select All" header state.
  const allFilteredSelected = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    const selectedIds = new Set(selected.map((s) => s.id));
    return filteredOptions.every((o) => selectedIds.has(o.id));
  }, [filteredOptions, selected]);

  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;
  const isReady = !isSummaryLoading && !isEmptyView && options.length > 0;

  const label = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.accounts.labelBank',
    defaultMessage: 'Bank account',
  });

  const handleOpen = useCallback(() => {
    setChecked(accountIds.length === 0 ? null : options.filter((o) => accountIds.includes(o.id)));
    setOpen(true);
  }, [accountIds, options]);

  // Commit once on close, mirroring BankFeedSetColumnFilter's apply semantics:
  //  - active search → commit only the selections visible under it (hidden selections are
  //    dropped); if nothing visible is selected, closing must NOT wipe the applied filter.
  //  - no search → all-or-none → [] (all), subset → its account_ids.
  // Reset the draft to null on close so the closed-state value is always the full option
  // set — that keeps the field label shrunk and the "All"/chips trigger rendering correctly.
  const handleClose = useCallback(() => {
    setOpen(false);
    setInputValue('');
    if (search.length > 0 && visibleSelected.length === 0) {
      setChecked(null);
      return;
    }
    const picked = search.length > 0 ? visibleSelected : selected;
    const wire = picked.length === 0 || picked.length === options.length ? [] : picked.map((o) => o.id);
    setToolbarFilters((prev) => (sameIds(prev.accountIds, wire) ? prev : { ...prev, accountIds: wire }));
    setChecked(null);
  }, [search, visibleSelected, selected, options, setToolbarFilters]);

  // Clear (X) in the field — resets the filter to "All" without opening the popup
  // (stopPropagation keeps the field click from toggling the dropdown open).
  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setChecked(null);
      setToolbarFilters((prev) => (prev.accountIds.length === 0 ? prev : { ...prev, accountIds: [] }));
    },
    [setToolbarFilters],
  );

  // Keep the typed search across a selection: MUI fires onInputChange with reason 'reset'
  // when an option is toggled, which would otherwise clear the field. Only react to actual
  // typing ('input') and the clear button ('clear').
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
      if (reason === 'input') setInputValue(value);
      else if (reason === 'clear') setInputValue('');
    },
    [],
  );

  // Scope Select All to the current search (BankFeedSetColumnFilter / Excel parity): it
  // toggles only the filtered options, preserving selections hidden by the query.
  const handleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredOptions.map((o) => o.id));
      setChecked(selected.filter((s) => !filteredIds.has(s.id)));
      return;
    }
    const selectedIds = new Set(selected.map((s) => s.id));
    setChecked([...selected, ...filteredOptions.filter((o) => !selectedIds.has(o.id))]);
  }, [allFilteredSelected, filteredOptions, selected]);

  // "Select All" header above the option list (outside the option set, so the search filter
  // does not hide it). onMouseDown preventDefault keeps the popup open on click.
  const PaperWithSelectAll = useMemo(() => {
    const AccountsPaper = ({ children, ...paperProps }: PaperProps) => (
      <Paper {...paperProps}>
        {/* Rendered as a MenuItem (component="div", since it sits outside the option
            <ul>) so it gets the same hover affordance as the option rows. onMouseDown
            preventDefault keeps the popup open on click; a bottom divider separates it
            from the list. Checked/indeterminate are scoped to the current search. */}
        <MenuItem
          component="div"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSelectAll}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Checkbox
            icon={ICON}
            checkedIcon={CHECKED_ICON}
            checked={allFilteredSelected}
            indeterminate={!allFilteredSelected && visibleSelected.length > 0}
            sx={{ mr: 1, p: 0 }}
          />
          {intl.formatMessage({
            id: 'company.bankFeed.transactions.toolbar.accounts.selectAll',
            defaultMessage: 'Select All',
          })}
        </MenuItem>
        {children}
      </Paper>
    );
    return AccountsPaper;
  }, [handleSelectAll, allFilteredSelected, visibleSelected.length, intl]);

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
      option: BankFeedSummaryAccount,
      { selected: isSelected }: AutocompleteRenderOptionState,
    ) => {
      const { key, ...liProps } = props;
      return (
        <li key={key} {...liProps}>
          <Checkbox icon={ICON} checkedIcon={CHECKED_ICON} checked={isSelected} sx={{ mr: 1, p: 0 }} />
          <Avatar
            src={option.picture ?? undefined}
            alt={option.label}
            sx={{ width: 24, height: 24, mr: 1, flexShrink: 0 }}
          />
          <Tooltip title={option.label} placement="right">
            <Box component="span" sx={ellipsisSx}>
              {option.label}
            </Box>
          </Tooltip>
        </li>
      );
    },
    [],
  );

  const renderValue = useCallback(() => {
    if (open) return null;
    // Empty wire = all accounts → "All" (no chips), matching the VendorExpenses idle state.
    if (accountIds.length === 0) {
      return intl.formatMessage({
        id: 'company.bankFeed.transactions.toolbar.accounts.all',
        defaultMessage: 'All',
      });
    }
    const picked = options.filter((o) => accountIds.includes(o.id));
    return picked.length > 0 ? <AccountChips picked={picked} /> : null;
  }, [open, accountIds, options, intl]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={label}
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {accountIds.length > 0 && !open && (
                  // onMouseDown preventDefault + stopPropagation so clicking ✕ neither focuses
                  // the input nor bubbles to the Autocomplete's open handler (which fires on
                  // mousedown, before onClick) — mirrors MUI's own clearIndicator behaviour.
                  <IconButton
                    size="small"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={handleClear}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                {isSummaryLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    ),
    [label, isSummaryLoading, accountIds, open, handleClear],
  );

  return (
    <Autocomplete<BankFeedSummaryAccount, true, true, false>
      multiple
      disableCloseOnSelect
      disableClearable
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={!isReady}
      size="small"
      // flexWrap nowrap keeps the collapsed chip + counter on a single line; the popper is
      // widened past the input via slotProps below.
      sx={{
        width: ACCOUNTS_SELECT_WIDTH,
        backgroundColor: 'common.white',
        '& .MuiInputBase-root': { flexWrap: 'nowrap' },
        // Left inset for the value (chips / "All"). Matches MUI's own selector specificity
        // (.MuiOutlinedInput-root.MuiInputBase-sizeSmall sets padding-left: 6px); a plain
        // .MuiInputBase-root rule would lose on specificity.
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { pl: 1.75 },
        // Collapse the empty input when closed so the chip claims the full row instead of the
        // input reserving space; restored to MUI's default when open for normal search typing.
        '& .MuiAutocomplete-input:first-of-type': open ? {} : { width: 0, minWidth: 0, p: 0 },
      }}
      options={options}
      // Take over filtering so the rendered list matches the `filteredOptions` the
      // Select All / visible-selection logic is computed against (no drift).
      filterOptions={() => filteredOptions}
      value={selected}
      onChange={(_event, value) => setChecked(value)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      slots={{ paper: PaperWithSelectAll }}
      // bottom-start pins the (wider) popup to the input's bottom-left edge instead of
      // centring it under the input.
      slotProps={{ popper: { placement: 'bottom-start', style: { width: ACCOUNTS_POPUP_WIDTH } } }}
      renderOption={renderOption}
      renderValue={renderValue}
      renderInput={renderInput}
    />
  );
};

export default BankAccountsFilter;
